import { toast } from 'react-toastify';
import { expandPlans } from './monthlyRegistrationHelpers';

const APP_CLIENT_ID = "RENTAL_REGISTER_DEV";
const BASE64_KEY = "AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=";
const DEV_BASE_URL = "https://apitransientdev.parkquility.com";

const env = process.env.REACT_APP_ENVIRONMENT;
const isDev = env === "DEV";

const encryptPayload = async () => {
  const payload = { timestamp: Date.now() };
  const jsonText = JSON.stringify(payload);
  const keyBytes = Uint8Array.from(atob(BASE64_KEY), c => c.charCodeAt(0));

  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    "AES-GCM",
    false,
    ["encrypt"]
  );

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoder = new TextEncoder();
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoder.encode(jsonText)
  );

  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return btoa(String.fromCharCode(...combined));
};

const getAuthHeaders = async () => {
  const token = await encryptPayload();
  return {
    'Content-Type': 'application/json',
    'app_client_id': APP_CLIENT_ID,
    'token': token
  };
};

const getApiUrl = (baseUrl, path) => {
  return isDev ? `${DEV_BASE_URL}${path}` : `${baseUrl}${path}`;
};

const fetchApi = async (baseUrl, path, options = {}) => {
  const url = getApiUrl(baseUrl, path);
  if (isDev) {
    const headers = await getAuthHeaders();
    return fetch(url, { ...options, headers: { ...headers, ...options.headers } });
  }
  return fetch(url, options);
};

export const getArenaDetails = async (arenaHash, baseUrl, urlParams, setStateCallback, getMonthlyPlansCallback) => {
  try {
    const response = await fetchApi(baseUrl, `/parking/arena/details?arena_hash=${arenaHash}`);
    const resp = await response.json();
    
    if (resp.data && resp.status === 'SUCCESS') {
      const data = resp.data;
      const is45SECase = data.id === 5832;
      const is207CollegeSt = data.id === 5999;
      const is700BiscayneLot = data.id === 6029;
      let showTemplate = urlParams.get('pricing_type');
      showTemplate = is45SECase && showTemplate == "city_national_bank" ? false : showTemplate;
      
      setStateCallback(
        {
          timeZone: data.timezone,
          parkingId: data.id,
          is45SECase,
          showTemplate,
          arenaName: data.name,
          lotType: data.lot_types,
          isEnforcement: data.enforcement_enabled,
          is700BiscayneLot,
          is207CollegeSt,
          currentStep: 1,
          allowAlternateVehicle: data.allow_alternate_vehicle || false,
          alternateVehicleLimit: data.alternate_vehicle_limit || 0
        },
        () => getMonthlyPlansCallback(data.id)
      );
    } else {
      const errorMessage = (resp.error && resp.error.reason) || 'Something went wrong';
      setStateCallback({
        loading: false,
        error: errorMessage
      });
      toast.error(errorMessage);
    }
  } catch (error) {
    setStateCallback({
      loading: false,
      error: 'Something went wrong'
    });
    toast.error('Something went wrong');
  }
};

export const getMonthlyPlans = async (parkingId, baseUrl, state, setStateCallback) => {
  try {
    const response = await fetchApi(baseUrl, `/parking/monthly/pricing/list?parking_id=${parkingId}`);
    const resp = await response.json();
    
    if (resp.data && resp.status === 'SUCCESS') {
      let respPricingCopy = resp.data.pricing && resp.data.pricing.length ? JSON.parse(JSON.stringify(resp.data.pricing)) : [];
      
      if(state.showTemplate){
        let templateName = state.showTemplate.replaceAll('_', ' ').toLowerCase();
        respPricingCopy.forEach(obj => {
          if(obj.pricing_data)
          obj.pricing_data.visible = obj.pricing_type && obj.pricing_type.toLowerCase() === templateName  ? true : false; 
        })
      }
      
      const originalPassList = !resp.data.new_subscription_allowed ? [] : respPricingCopy && respPricingCopy.length ?
                                  state.visible !== "false" ?
                                  respPricingCopy.filter(item => item.pricing_data && (!item.pricing_data.hasOwnProperty('visible') || item.pricing_data.visible)) 
                                  : respPricingCopy.filter(item => item.pricing_data && item.pricing_data.visible === false) 
                                  : [];
      
      const filteredByStatus = originalPassList.filter(item => item.status === 'active');
      const expandedPlans = expandPlans(filteredByStatus);
      
      setStateCallback({
        passList: expandedPlans,
        originalPassList: expandedPlans,
        loading: false,
        isAutoApproval: resp.data.request_auto_approved || false
      });
    } else {
      const errorMessage = (resp.error && resp.error.reason) || 'Something went wrong';
      setStateCallback({
        loading: false,
        error: errorMessage
      });
      toast.error(errorMessage);
    }
  } catch (error) {
    setStateCallback({
      loading: false,
      error: 'Something went wrong'
    });
    toast.error('Something went wrong');
  }
};

export const calculateAccountPrice = async (state, baseUrl, urlParams, setStateCallback) => {
  let params = '';
  const { selectedPlan, no_of_spots, parkingId } = state;
  
  if (selectedPlan && selectedPlan.pricing_data && selectedPlan.pricing_data.is_reserve_spot) {
    params = `parking_id=${parkingId}&slot_count=${no_of_spots}&pass_type=${selectedPlan ? selectedPlan.pricing_type : ''}&reserve_spot=true`;
  } else {
    params = `parking_id=${parkingId}&slot_count=${no_of_spots}&pass_type=${selectedPlan ? selectedPlan.pricing_type : ''}&reserve_spot=false`;
  }
  
  setStateCallback({ loadingPricing: true });
  
  try {
    const response = await fetchApi(baseUrl, `/parking/monthly/bulk/pricing?${params}`);
    const resp = await response.json();
    
    if (resp.data && resp.status === 'SUCCESS') {
      const subscriptionType = state.selectedPlan?.subscriptionType || 'individual';
      const isPayment = urlParams.get('pay') === "true" && subscriptionType === 'individual' ? true : subscriptionType === 'individual' && resp.data.is_payment ? true : false;
      
      setStateCallback({
        monthlyAccountPrice: resp.data.rental_amount / 100,
        rentalFee: resp.data.rental_fee,
        convFee: resp.data.conv_fee,
        reservedSpot: resp.data.reserved_spot,
        isPayment: isPayment,
        terms: resp.data.terms ? resp.data.terms : [],
        loadingPricing: false,
        currentStep: 2
      });
    } else {
      const errorMessage = resp.error && resp.error.reason ? resp.error.reason : 'Something went wrong';
      toast.error(errorMessage);
      setStateCallback({ loadingPricing: false });
    }
  } catch (error) {
    toast.error('Something went wrong');
    setStateCallback({ loadingPricing: false });
  }
};

export const submitRequest = async (submitRequestData, baseUrl, env, state, setStateCallback, manageBodyBackground) => {
  const istypeAccount = submitRequestData.type === 'account';
  const path = istypeAccount
    ? `/parking/monthly/account/request`
    : `/parking/monthly/user/request`;
  
  setStateCallback({ submittingForm: true });
  
  try {
    const response = await fetchApi(baseUrl, path, {
      method: 'POST',
      body: JSON.stringify(submitRequestData)
    });
    const resp = await response.json();
    
    if (resp.status === 'SUCCESS' && resp.data) {
      const subscriptionId = istypeAccount ? resp.data.account_hash : resp.data.pass_hash;
      const isAccountAndPayByIndividual = istypeAccount && submitRequestData.payment_type === 'individual';
      
      if (isAccountAndPayByIndividual) {
        manageBodyBackground(false);
        setStateCallback({ booked: true, submittingForm: false });
        return;
      }
      
      if (!state.isAutoApproval) {
        manageBodyBackground(false);
        setStateCallback({ booked: true, submittingForm: false });
        return;
      }
      
      if(env === 'prod'){
        window.location.href = istypeAccount ? `https://monthly.parkquility.com/account?id=${subscriptionId}` : 
                                            `https://monthly.parkquility.com?id=${subscriptionId}`;
      }else{
        window.location.href = istypeAccount ? `https://${env}monthly.parkquility.com/account?id=${subscriptionId}` : 
                                            `https://${env}monthly.parkquility.com?id=${subscriptionId}`
      }
    } else {
      const errMsg = resp.error && resp.error.reason ? resp.error.reason : 'Something went wrong';
      setStateCallback({ submittingForm: false });
      toast.error(errMsg);
    }
  } catch (error) {
    setStateCallback({ submittingForm: false });
    toast.error('Something went wrong');
  }
};
